/**
 * /auth/callback — post-email-confirmation (and future OAuth) landing.
 *
 * `auth/signup.tsx` sets `emailRedirectTo: <origin>/auth/callback`, so Supabase
 * sends the user here after they click the confirmation link in their signup
 * email. The Supabase client (`detectSessionInUrl: true`) consumes the session
 * from the URL hash; this route waits for it, then routes the user onward:
 *   - session + `app_metadata.tenant_id`     → landingRouteForRole(role)
 *                                               (already onboarded)
 *   - session, no tenant, persona 'firm'      → `/onboarding/firm`
 *   - session, no tenant, individual/absent   → `/onboarding/individual`
 *   - no session (used/expired link)          → `/auth/login`
 *
 * PERSONA-ROUTED LANDING (2026-06-21): the persona chosen at signup decides
 * which EXISTING onboarding a confirmed, no-tenant session enters. It rides on
 * `session.user.user_metadata.account_type` (set by the signup persona chooser):
 *   - 'firm'  → `/onboarding/firm` — the firm-bootstrap step that creates the
 *     tenant + firm-admin, then chains MFA enrol → `/admin` (the firm admin
 *     console — NOT the end-customer `/app/setup` document-first questionnaire).
 *   - 'individual' / absent / legacy → `/onboarding/individual` — the B2C
 *     5-step doc-first wrapper (verify email → subscription → MFA → upload the
 *     four mandatory docs → confirm), which REPLACES the older 9-step
 *     `/onboarding/welcome` wizard for new individuals (accountType 'direct').
 *
 * Absent metadata defaults to the individual flow: never strand an older
 * account (signed up before the chooser existed) on the firm bootstrap. A brief
 * earlier cut (commit 1a9fc4d) routed EVERY no-tenant session to
 * `/onboarding/firm`, which orphaned the individual path — this is that fix.
 *
 * Before this route existed, the confirmation link landed on the marketing
 * root (`/#`) — the user was signed in but stranded with no onward path and no
 * favicon. (GA polish 2026-06-05.)
 *
 * NB: the Supabase project's Auth "Redirect URLs" allowlist must include
 * `https://app.elreign.io/auth/callback` (or `https://app.elreign.io/**`), else
 * Supabase ignores `emailRedirectTo` and falls back to the Site URL (the
 * marketing root). That allowlist is now correct in prod (2026-07-03), so a
 * confirmed user reaches THIS route — but the marketing root (routes/index.tsx)
 * additionally carries a session-aware safety-net that re-routes a signed-in,
 * pre-onboarding user to onboarding, so an allowlist regression can no longer
 * strand them on a signed-in-but-dead-end marketing page.
 */
import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AuthLayout } from '../../components/layouts/auth-layout';
import { supabase } from '../../lib/supabase';
import {
  landingRouteForRole,
  landingClaimsFromAccessToken,
  preOnboardingRouteForSession,
} from '../../lib/auth-landing';
import { ApiError, useSelfJoinFirm } from '../../lib/api';
import { readPendingFirmSlug, clearPendingFirmSlug } from '../../lib/firm-self-signup';
import { useIndividualOnboardingStore } from '../../lib/individual-onboarding-store';

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallback,
});

function AuthCallback() {
  const navigate = useNavigate();
  const [expired, setExpired] = useState(false);
  // Client Model B: an honest, terminal error when the firm won't accept the
  // self-join (opt-in turned off / unknown firm). We stop here rather than
  // stranding the user in an onboarding flow that can't attach them.
  const [joinError, setJoinError] = useState<string | null>(null);
  const selfJoin = useSelfJoinFirm();
  const setBillingOwner = useIndividualOnboardingStore((s) => s.setBillingOwner);

  useEffect(() => {
    let settled = false;

    // Client Model B: attach a freshly-confirmed client to a firm tenant BEFORE
    // routing into individual onboarding. Only runs when a pending firm slug
    // survived the confirm round-trip (?firm= on this callback URL or the
    // persisted value). 409 ALREADY_IN_TENANT → just proceed (idempotent). A
    // 403/404 is surfaced honestly and we stop (no onboarding into a firm that
    // refuses the join).
    const selfJoinThenOnboard = async (firmSlug: string) => {
      try {
        const res = await selfJoin.mutateAsync({ firmSlug });
        // Remember who pays so the onboarding wrapper can skip (or keep) the
        // paid subscription step.
        setBillingOwner(res.profile.billingOwner);
        clearPendingFirmSlug();
        // Refresh so the JWT carries the firm tenant_id + role:'user' the
        // onboarding setup endpoints require.
        try {
          await supabase.auth.refreshSession();
        } catch {
          /* non-fatal — the onboarding wrapper re-checks the session itself */
        }
        void navigate({ to: '/onboarding/individual' });
      } catch (err) {
        // Already attached to this (or another) tenant → proceed into onboarding.
        if (err instanceof ApiError && err.status === 409) {
          clearPendingFirmSlug();
          void navigate({ to: '/onboarding/individual' });
          return;
        }
        // Opt-in off / not a firm / unknown firm → honest terminal message.
        if (
          err instanceof ApiError &&
          (err.code === 'SELF_SIGNUP_DISABLED' ||
            err.code === 'NOT_A_FIRM' ||
            err.code === 'FIRM_NOT_FOUND')
        ) {
          clearPendingFirmSlug();
          setJoinError(
            'This firm is not currently accepting new client sign-ups. Your account was ' +
              'created. Please contact the firm, or sign in to continue on your own.',
          );
          return;
        }
        // Any other failure (network, 400, 429, 500): don't block the user from
        // the app — proceed into onboarding, which will re-derive their state.
        void navigate({ to: '/onboarding/individual' });
      }
    };

    const routeOnward = (
      session:
        | { access_token: string; user?: { user_metadata?: Record<string, unknown> } | null }
        | null,
    ) => {
      if (settled) return;
      settled = true;
      if (!session) {
        void navigate({ to: '/auth/login' });
        return;
      }
      const { tenantId, role } = landingClaimsFromAccessToken(session.access_token);
      if (tenantId) {
        // Already onboarded → straight to the role's home.
        void navigate({ to: landingRouteForRole(role) });
        return;
      }
      // No tenant yet (fresh, confirmed signup). Client Model B FIRST: if a firm
      // slug survived the confirm, attach to that firm, then onboard. This is
      // the ONLY new branch — the persona routing below is unchanged.
      const firmSlug = readPendingFirmSlug(
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null,
      );
      if (firmSlug) {
        void selfJoinThenOnboard(firmSlug);
        return;
      }
      // Otherwise route by the persona chosen at signup. Anything other than an
      // explicit 'firm' (incl. 'individual', absent, or any legacy value) falls
      // to the individual onboarding wrapper — the transparent 5-step B2C flow
      // (provision → subscription → MFA → documents → confirm), which REPLACES
      // the older `/onboarding/welcome` wizard for the fresh-individual path.
      // Shared resolver (auth-landing.ts) — same mapping the /app layout guard
      // and the marketing landing safety-net use.
      void navigate({ to: preOnboardingRouteForSession(session) });
    };

    // The session may already be set (detectSessionInUrl ran at client init)…
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) routeOnward(data.session);
    });
    // …or it arrives via the auth-state change once the hash is parsed.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) routeOnward(session);
    });
    // Fallback: a used/expired link yields no session — show a clear message,
    // then send the user to sign in.
    const timer = setTimeout(() => {
      if (!settled) {
        setExpired(true);
        setTimeout(() => routeOnward(null), 1800);
      }
    }, 6000);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timer);
    };
    // selfJoin / setBillingOwner are stable refs; navigate is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (joinError) {
    return (
      <AuthLayout
        eyebrow="Account created"
        heading="Almost there."
        sub="We created your account, but couldn’t attach it to the firm."
      >
        <div
          role="alert"
          style={{
            padding: '12px 14px',
            borderRadius: 8,
            background: 'var(--negative-soft, rgba(220,38,38,0.06))',
            border: '1px solid rgba(220,38,38,0.3)',
            color: '#991B1B',
            fontSize: 13.5,
            lineHeight: 1.5,
            marginBottom: 16,
          }}
        >
          {joinError}
        </div>
        <a
          href="/auth/login"
          style={{ color: 'var(--brand-primary, var(--primary-600))', fontWeight: 600, fontSize: 14 }}
        >
          Go to sign in
        </a>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow="Almost there"
      heading={expired ? 'This link has expired.' : 'Confirming your email'}
      sub={
        expired
          ? 'For your security, confirmation links can only be used once. Taking you to sign in…'
          : 'One moment while we verify your account and sign you in.'
      }
    >
      <div
        aria-live="polite"
        style={{ fontSize: 14, color: 'var(--ink-3)', textAlign: 'center', padding: '8px 0' }}
      >
        {expired ? 'Redirecting…' : 'Please wait…'}
      </div>
    </AuthLayout>
  );
}
